<script>
import { GlButton } from '@gitlab/ui';
import { s__ } from '~/locale';
import DuoChatQuickAction from 'ee/ai/shared/widgets/duo_chat_quick_action.vue';
import DuoWorkItemToMrAction from 'ee/ai/shared/widgets/duo_work_item_to_mr_action.vue';
import { DUO_CHAT_AGENT_PLANNER } from '~/ai/constants';

// TODO: keep in sync with the same prompt in agent_plan_panel.vue (or factor
// out into a shared constants module once the wording stabilises).
const AGENTIC_PROMPT = s__(
  `AgentPlan|You are here to help refine a plan for the user from the Work Item context to a Markdown output with key information required for Agent to pick off the work. The template should have a Why, How, What format with key insights and list of steps. If the issue is lacking details, start by reading the comments. Then, ask clarifying questions to the users in Chat until you are confident. If there are pending questions, please preserve them inside the plan. Then, please proceed with updating the work item 'Workplan' widget with your output. You can erase everything was there before and add your new plan entirely. Don't preserve existing notes if they dont align with what you wrote. If there is enough information but the title or description is lacking, please propose to update it minimally with key insights. Try to preserve anything that is already present, just add on top and dont repeat the full plan. What is important is syncing up enough to preserve the intent and adding data where we had none before.`,
);

// Extra goal context passed to the "Generate MR with Duo" action so the
// agent treats the saved workplan as the primary spec instead of starting
// from the work-item description. Copied verbatim from the inline widget
// version that lived in the orchestrator pre-side-panel.
const WORKPLAN_GOAL_PREFIX =
  'The work item has an agent_plan widget containing a workplan authored for you. Treat that plan as the primary specification: define your tasks and approach from it, and only fall back to the description or comments for additional context when the plan is unclear.';

export default {
  name: 'AgentPlanInlineRow',
  components: {
    GlButton,
    DuoChatQuickAction,
    DuoWorkItemToMrAction,
  },
  props: {
    hasContent: {
      type: Boolean,
      required: false,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPanelOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    workItemId: {
      type: String,
      required: false,
      default: null,
    },
    showGenerateMrAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    workItemIid: {
      type: String,
      required: false,
      default: null,
    },
    workItemType: {
      type: String,
      required: false,
      default: null,
    },
    workItemWebUrl: {
      type: String,
      required: false,
      default: '',
    },
    projectPath: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['open', 'request-open', 'chat-opened'],
  computed: {
    statusLabel() {
      if (!this.hasContent) {
        return this.canUpdate ? s__('AgentPlan|Not started') : s__('AgentPlan|No workplan');
      }
      return s__('AgentPlan|Ready');
    },
    showCreateAction() {
      return !this.hasContent && this.canUpdate;
    },
    showToggleAction() {
      return this.hasContent;
    },
    showDuoAction() {
      return !this.hasContent && this.canUpdate && Boolean(this.workItemId);
    },
  },
  duoQuickAction: {
    command: {
      agent: { name: DUO_CHAT_AGENT_PLANNER },
      agenticPrompt: AGENTIC_PROMPT,
    },
    tracking: { label: 'create_work_plan' },
    buttonOptions: { variant: 'confirm', size: 'small' },
  },
  WORKPLAN_GOAL_PREFIX,
};
</script>

<template>
  <div
    data-testid="agent-plan-inline-row"
    class="gl-relative gl-flex gl-items-center gl-justify-between gl-gap-3 gl-rounded-xl gl-border-1 gl-border-solid gl-border-default gl-p-4"
  >
    <div class="gl-flex-row">
      <span
        class="gl-relative gl-mb-2 gl-block gl-h-2 gl-w-5 gl-rounded-pill"
        :style="{
          backgroundColor: hasContent
            ? 'var(--gl-icon-color-success)'
            : 'var(--gl-icon-color-subtle)',
        }"
        role="status"
        :aria-label="statusLabel"
      ></span>
      <div class="gl-flex gl-items-center gl-gap-2">
        <span class="gl-font-bold gl-text-default">{{ s__('AgentPlan|Workplan:') }}</span>
        <span class="gl-text-subtle">{{ statusLabel }}</span>
      </div>
    </div>
    <div class="gl-flex gl-items-center gl-gap-2">
      <span v-if="showDuoAction" @click="$emit('request-open')">
        <duo-chat-quick-action
          :resource-id="workItemId"
          :button-text="s__('AgentPlan|Generate workplan with Duo')"
          :command="$options.duoQuickAction.command"
          :tracking-info="$options.duoQuickAction.tracking"
          :button-options="$options.duoQuickAction.buttonOptions"
          data-testid="inline-generate-with-duo-button"
          @chat-opened="$emit('chat-opened')"
        />
      </span>
      <duo-work-item-to-mr-action
        v-if="showGenerateMrAction"
        :project-path="projectPath"
        :work-item-id="workItemIid"
        :work-item-type="workItemType"
        :work-item-web-url="workItemWebUrl"
        :additional-goal-context="$options.WORKPLAN_GOAL_PREFIX"
        data-testid="inline-generate-mr-with-duo-button"
      />
      <gl-button
        v-if="showCreateAction"
        category="primary"
        variant="default"
        size="small"
        data-testid="open-agent-plan-button"
        @click="$emit('open')"
      >
        {{ s__('AgentPlan|Create workplan') }}
      </gl-button>
      <gl-button
        v-else-if="showToggleAction"
        :aria-pressed="String(isPanelOpen)"
        size="small"
        category="secondary"
        data-testid="open-agent-plan-button"
        @click="$emit('open')"
      >
        {{ s__('AgentPlan|View workplan') }}
      </gl-button>
    </div>
  </div>
</template>
